import React, { useState, useEffect } from 'react'
import Layout from './Layout'

const PayoutConfiguration = () => {
  const [paymentMethod, setPaymentMethod] = useState('ach')
  const [email, setEmail] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [routingNumber, setRoutingNumber] = useState('')
  const [accountType, setAccountType] = useState('CHECKING')
  const [accountName, setAccountName] = useState('')
  const [status, setStatus] = useState({ message: '', isError: false })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasBankAccount, setHasBankAccount] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [digitalCheckApproved, setDigitalCheckApproved] = useState(null)
  const [payee, setPayee] = useState('')

  // Mailed check form data
  const [mailedCheckData, setMailedCheckData] = useState({
    payee: '',
    mailing_name: '',
    mailing_street: '',
    mailing_city: '',
    mailing_state: '',
    mailing_zip: '',
  })

  useEffect(() => {
    const fetchLawFirmData = async () => {
      try {
        const response = await fetch('/collectors/api/get_law_firm')
        if (response.ok) {
          const data = await response.json()
          if (data.law_firm) {
            // Pre-fill form with existing data
            if (data.law_firm.digital_check_email) {
              setEmail(data.law_firm.digital_check_email)
            }
            // Check if bank_id exists
            if (data.law_firm.bank_id) {
              setHasBankAccount(true)
            }

            // Set digital check approved state
            setDigitalCheckApproved(data.law_firm.digital_check_approved)

            // Set payee for ACH forms
            if (data.law_firm.payee) {
              setPayee(data.law_firm.payee)
            }

            // Pre-fill mailed check data
            setMailedCheckData({
              payee: data.law_firm.payee || '',
              mailing_name: data.law_firm.mailing_name || '',
              mailing_street: data.law_firm.mailing_street || '',
              mailing_city: data.law_firm.mailing_city || '',
              mailing_state: data.law_firm.mailing_state || '',
              mailing_zip: data.law_firm.mailing_zip || '',
            })
          }
        }
      } catch (error) {
        console.error('Error fetching law firm data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchLawFirmData()
  }, [])

  const resetForm = () => {
    setAccountNumber('')
    setRoutingNumber('')
    setAccountType('CHECKING')
    setAccountName('')
  }

  const handleToggleChange = method => {
    setPaymentMethod(method)
    if (method === 'ach') {
      resetForm()
    }
    setStatus({ message: '', isError: false })
  }

  const handleMailedCheckChange = e => {
    const { name, value } = e.target
    setMailedCheckData(prev => ({
      ...prev,
      [name]: value,
    }))
    // Keep payee in sync between forms
    if (name === 'payee') {
      setPayee(value)
    }
  }

  const handleRemoveMailedCheckData = async () => {
    if (!confirm('Are you sure you want to remove your payment details? This cannot be undone.')) {
      return
    }

    setIsSubmitting(true)
    setStatus({ message: '', isError: false })

    try {
      const response = await fetch('/collectors/api/remove_firm_payment_details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      })

      const data = await response.json()
      if (response.ok) {
        // Clear the form fields after successful removal
        setMailedCheckData({
          payee: '',
          mailing_name: '',
          mailing_street: '',
          mailing_city: '',
          mailing_state: '',
          mailing_zip: '',
        })
        setStatus({
          message: data.message || 'Payment details removed successfully!',
          isError: false,
        })
      } else {
        setStatus({ message: data.message || 'Failed to remove payment details', isError: true })
      }
    } catch (error) {
      setStatus({ message: error.message || 'An error occurred', isError: true })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMailedCheckSubmit = async e => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus({ message: '', isError: false })

    // Validate required fields for mailed check
    const requiredFields = [
      'payee',
      'mailing_street',
      'mailing_city',
      'mailing_state',
      'mailing_zip',
    ]
    const missingFields = requiredFields.filter(
      field => !mailedCheckData[field] || mailedCheckData[field].trim() === ''
    )

    if (missingFields.length > 0) {
      setStatus({
        message: `Please fill in all required fields: ${missingFields.join(', ').replace(/_/g, ' ')}`,
        isError: true,
      })
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('/collectors/api/update_firm_payment_details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          law_firm: {
            ...mailedCheckData,
            digital_check_approved: digitalCheckApproved,
          },
        }),
      })

      const data = await response.json()
      if (response.ok) {
        setStatus({ message: 'Payment details updated successfully!', isError: false })
        // Sync payee state with mailed check data
        setPayee(mailedCheckData.payee)
      } else {
        setStatus({ message: data.message || 'An error occurred', isError: true })
      }
    } catch (error) {
      setStatus({ message: error.message || 'An error occurred', isError: true })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConnectedACHUpdate = async e => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus({ message: '', isError: false })

    // Validation
    if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setStatus({ message: 'Please enter a valid email address', isError: true })
      setIsSubmitting(false)
      return
    }

    if (!payee || payee.trim().length === 0) {
      setStatus({ message: 'Please enter a payee name', isError: true })
      setIsSubmitting(false)
      return
    }

    try {
      // First update the payee
      const payeeResponse = await fetch('/collectors/api/update_firm_payment_details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          law_firm: {
            payee: payee,
          },
        }),
      })

      if (!payeeResponse.ok) {
        const payeeData = await payeeResponse.json()
        setStatus({ message: payeeData.message || 'Failed to update payee name', isError: true })
        setIsSubmitting(false)
        return
      }

      // Then update email and preferences
      const response = await fetch('/collectors/api/digital_check_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          email: email,
          digital_check_approved: digitalCheckApproved,
        }),
      })

      if (response.ok) {
        setStatus({
          message: 'Payee, email and payment preferences updated successfully!',
          isError: false,
        })
      } else {
        const data = await response.json()
        setStatus({
          message: data.message || 'Failed to update email and preferences',
          isError: true,
        })
      }
    } catch (error) {
      setStatus({ message: error.message || 'An error occurred', isError: true })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemoveBankAccount = async () => {
    if (
      !window.confirm(
        'Are you sure you want to remove your bank account? This will revert to using mailed checks for payments.'
      )
    ) {
      return
    }

    setIsRemoving(true)
    setStatus({ message: '', isError: false })

    try {
      const response = await fetch('/collectors/api/remove_bank_account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      })

      if (response.ok) {
        setHasBankAccount(false)
        setDigitalCheckApproved(false) // Reset default payment method when bank account is removed
        setStatus({
          message:
            'Bank account removed successfully. Future payments will be sent as mailed checks.',
          isError: false,
        })
      } else {
        const data = await response.json()
        setStatus({ message: data.message || 'Failed to remove bank account', isError: true })
      }
    } catch (error) {
      setStatus({ message: error.message || 'An error occurred', isError: true })
    } finally {
      setIsRemoving(false)
    }
  }

  const handleAchSubmit = async e => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus({ message: '', isError: false })

    // Validation
    if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setStatus({ message: 'Please enter a valid email address', isError: true })
      setIsSubmitting(false)
      return
    }

    if (!payee || payee.trim().length === 0) {
      setStatus({ message: 'Please enter a payee name', isError: true })
      setIsSubmitting(false)
      return
    }

    if (!/^\d{9}$/.test(routingNumber)) {
      setStatus({ message: 'Routing number must be 9 digits', isError: true })
      setIsSubmitting(false)
      return
    }

    if (accountNumber.trim().length < 4) {
      setStatus({ message: 'Please enter a valid account number', isError: true })
      setIsSubmitting(false)
      return
    }

    try {
      // First save the payee
      const payeeResponse = await fetch('/collectors/api/update_firm_payment_details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          law_firm: {
            payee: payee,
          },
        }),
      })

      if (!payeeResponse.ok) {
        const payeeData = await payeeResponse.json()
        setStatus({ message: payeeData.message || 'Failed to update payee name', isError: true })
        setIsSubmitting(false)
        return
      }

      // Then save the email
      const emailResponse = await fetch('/collectors/api/digital_check_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          email,
          digital_check_approved: hasBankAccount ? digitalCheckApproved : true,
        }),
      })

      if (!emailResponse.ok) {
        const emailData = await emailResponse.json()
        setStatus({ message: emailData.message || 'Failed to save email address', isError: true })
        setIsSubmitting(false)
        return
      }

      // Then save the bank account
      const bankResponse = await fetch('/collectors/api/bank_account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          account_number: accountNumber,
          routing_number: routingNumber,
          account_type: accountType,
          account_name: accountName,
        }),
      })

      if (bankResponse.ok) {
        resetForm()
        setStatus({ message: 'Bank account and email set up successfully!', isError: false })
        setHasBankAccount(true)
        // ACH is automatically set as default payment method when first created
        setDigitalCheckApproved(true)
      } else {
        const data = await bankResponse.json()
        setStatus({ message: data.message || 'An error occurred', isError: true })
      }
    } catch (error) {
      setStatus({ message: error.message || 'An error occurred', isError: true })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className='display-3 mb-4'>Payment Options</div>
        <p>Loading...</p>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className='display-3 mb-4'>Payment Options</div>

      <div className='mb-4'>
        <div className='btn-group' role='group' aria-label='Payment Method Selection'>
          <button
            type='button'
            className={`btn ${paymentMethod === 'ach' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => handleToggleChange('ach')}
          >
            ACH Direct Deposit
          </button>
          <button
            type='button'
            className={`btn ${paymentMethod === 'mailedCheck' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => handleToggleChange('mailedCheck')}
          >
            Mailed Check
          </button>
        </div>
      </div>

      {paymentMethod === 'mailedCheck' ? (
        <>
          <h2>Enter your firm's payment details here.</h2>
          <p className='lead text-muted mb-4'>
            If you're being paid by check, each check will be sent in accord with these payment
            details. Note that you may not enter a PO Box.
            <br />
            <br />
            For faster payment, set up ACH Direct Deposit.
          </p>

          <form onSubmit={handleMailedCheckSubmit} className='mt-4'>
            {status.message && (
              <div
                className={`alert ${status.isError ? 'alert-danger' : 'alert-success'} mt-3 mb-4`}
              >
                {status.message}
              </div>
            )}

            <div className='row g-3'>
              <div className='col-md-6'>
                {Object.entries({
                  payee: 'Payee Name',
                  mailing_name: 'Mailing Name (optional)',
                  mailing_street: 'Street Address',
                  mailing_city: 'City',
                }).map(([field, label]) => (
                  <div className='mb-3' key={field}>
                    <label htmlFor={field} className='form-label'>
                      {label}
                    </label>
                    <input
                      type='text'
                      className='form-control'
                      id={field}
                      name={field}
                      value={mailedCheckData[field]}
                      onChange={handleMailedCheckChange}
                      disabled={isSubmitting}
                    />
                  </div>
                ))}

                <div className='row'>
                  <div className='col-md-6 mb-3'>
                    <label htmlFor='mailing_state' className='form-label'>
                      State
                    </label>
                    <input
                      type='text'
                      className='form-control'
                      id='mailing_state'
                      name='mailing_state'
                      value={mailedCheckData.mailing_state}
                      onChange={handleMailedCheckChange}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className='col-md-6 mb-3'>
                    <label htmlFor='mailing_zip' className='form-label'>
                      ZIP Code
                    </label>
                    <input
                      type='text'
                      className='form-control'
                      id='mailing_zip'
                      name='mailing_zip'
                      value={mailedCheckData.mailing_zip}
                      onChange={handleMailedCheckChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {hasBankAccount && (
                  <div className='form-check mb-4'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='defaultPaymentMethodMailedCheck'
                      checked={digitalCheckApproved === false}
                      onChange={e => setDigitalCheckApproved(e.target.checked ? false : true)}
                      disabled={isSubmitting}
                    />
                    <label className='form-check-label' htmlFor='defaultPaymentMethodMailedCheck'>
                      Make this my default payment method
                    </label>
                  </div>
                )}

                <div className='d-flex gap-2'>
                  <button type='submit' className='btn btn-primary btn-lg' disabled={isSubmitting}>
                    {isSubmitting ? 'Updating...' : 'Update Payment Details'}
                  </button>
                  <button
                    type='button'
                    className='btn btn-outline-danger btn-lg'
                    onClick={handleRemoveMailedCheckData}
                    disabled={isSubmitting}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </form>
        </>
      ) : (
        <>
          {hasBankAccount ? (
            <>
              <h2>Bank Account Already Connected</h2>
              <p className='lead text-muted mb-4'>
                Your bank account is currently set up for ACH direct deposits. All future payments
                will be deposited directly to your connected bank account.
                <br />
                You can continue to receive payment notifications at your email address.
              </p>

              {status.message && (
                <div
                  className={`alert ${status.isError ? 'alert-danger' : 'alert-success'} mt-3 mb-4 w-50`}
                >
                  {status.message}
                </div>
              )}

              <form onSubmit={handleConnectedACHUpdate} className='mt-4 w-50'>
                <div className='form-group mb-3'>
                  <label htmlFor='payeeConnectedACH' className='form-label'>
                    Payee Name
                  </label>
                  <input
                    id='payeeConnectedACH'
                    type='text'
                    className='form-control form-control-lg'
                    placeholder='Law Firm Name'
                    value={payee}
                    onChange={e => setPayee(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                  <div className='form-text'>Name that will appear on payment checks</div>
                </div>

                <div className='form-group mb-3'>
                  <label htmlFor='emailConnectedACH' className='form-label'>
                    Email Address for Notifications
                  </label>
                  <input
                    id='emailConnectedACH'
                    type='email'
                    className='form-control form-control-lg'
                    placeholder='accounting@collector.com'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                  <div className='form-text'>Update your email for payment notifications</div>
                </div>

                {hasBankAccount && (
                  <div className='form-check mb-4'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='defaultPaymentMethodConnectedACH'
                      checked={digitalCheckApproved === true}
                      onChange={e => setDigitalCheckApproved(e.target.checked ? true : false)}
                      disabled={isSubmitting}
                    />
                    <label className='form-check-label' htmlFor='defaultPaymentMethodConnectedACH'>
                      Make this my default payment method
                    </label>
                  </div>
                )}

                <button
                  type='submit'
                  className='btn btn-primary btn-lg mb-3'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Payment Details'}
                </button>
              </form>

              <div className='mt-4'>
                <button
                  className='btn btn-danger btn-lg'
                  onClick={handleRemoveBankAccount}
                  disabled={isRemoving}
                >
                  {isRemoving ? 'Removing...' : 'Remove Bank Account'}
                </button>
                <p className='form-text mt-2'>
                  If you remove your bank account, you'll need to use mailed checks for receiving
                  payments.
                </p>
              </div>
            </>
          ) : (
            <>
              <h2>Set up ACH direct deposit to your trust account</h2>
              <p className='lead text-muted mb-4'>
                Receiving payment by ACH is the most secure method and strongly recommended. Provide
                your bank account details to receive payments through ACH direct deposit.
                <br />
                Your bank information is secured and not stored on our servers.
                <br />
                An email address is required for payment notifications and as a backup payment
                method.
              </p>

              <form onSubmit={handleAchSubmit} className='mt-4 w-50'>
                {status.message && (
                  <div
                    className={`alert ${status.isError ? 'alert-danger' : 'alert-success'} mt-3`}
                  >
                    {status.message}
                  </div>
                )}

                <div className='form-group mb-3'>
                  <label htmlFor='payeeACH' className='form-label'>
                    Payee Name
                  </label>
                  <input
                    id='payeeACH'
                    type='text'
                    className='form-control form-control-lg'
                    placeholder='Law Firm Name'
                    value={payee}
                    onChange={e => setPayee(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className='form-group mb-3'>
                  <label htmlFor='emailACH' className='form-label'>
                    Email Address for Notifications
                  </label>
                  <input
                    id='emailACH'
                    type='email'
                    className='form-control form-control-lg'
                    placeholder='accounting@collector.com'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className='form-group mb-3'>
                  <label htmlFor='accountName' className='form-label'>
                    Account Holder Name
                  </label>
                  <input
                    id='accountName'
                    type='text'
                    className='form-control form-control-lg'
                    placeholder='Full legal name on the account'
                    value={accountName}
                    onChange={e => setAccountName(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className='form-group mb-3'>
                  <label htmlFor='routingNumber' className='form-label'>
                    Routing Number
                  </label>
                  <input
                    id='routingNumber'
                    type='text'
                    className='form-control form-control-lg'
                    placeholder='9-digit routing number'
                    value={routingNumber}
                    onChange={e => setRoutingNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                    required
                    pattern='^\d{9}$'
                    maxLength={9}
                    disabled={isSubmitting}
                  />
                </div>

                <div className='form-group mb-3'>
                  <label htmlFor='accountNumber' className='form-label'>
                    Account Number
                  </label>
                  <input
                    id='accountNumber'
                    type='text'
                    className='form-control form-control-lg'
                    placeholder='Account number'
                    value={accountNumber}
                    onChange={e => setAccountNumber(e.target.value.replace(/\s/g, ''))}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className='form-group mb-3'>
                  <label htmlFor='accountType' className='form-label'>
                    Account Type
                  </label>
                  <select
                    id='accountType'
                    className='form-select form-select-lg'
                    value={accountType}
                    onChange={e => setAccountType(e.target.value)}
                    required
                    disabled={isSubmitting}
                  >
                    <option value='CHECKING'>Checking</option>
                    <option value='SAVINGS'>Savings</option>
                    <option value='BUSINESS'>IOLTA</option>
                  </select>
                </div>

                <button
                  type='submit'
                  className='btn btn-primary btn-lg mt-3'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Setting up...' : 'Set up ACH Direct Deposit'}
                </button>
              </form>
            </>
          )}
        </>
      )}
    </Layout>
  )
}

export default PayoutConfiguration
